"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  ArrowRight,
  Hospital,
  FileText,
  CalendarDays,
  Star,
  CheckCircle,
  BarChart3,
  Play,
  ChevronDown,
  Brain,
  DollarSign,
  MessageSquare,
} from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { useState } from "react"
import { DashboardShowcase } from "@/components/dashboard-showcase"
import { ClassyHeader } from "@/components/classy-header"

export default function ClinicManagementLanding() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const navigate = useNavigate();

  const features = [
    {
      icon: Brain,
      title: "AI-Powered Diagnostics",
      description:
        "Advanced machine learning algorithms assist in diagnosis, treatment recommendations, and early disease detection with 95% accuracy.",
    },
    {
      icon: BarChart3,
      title: "Intelligent Analytics",
      description:
        "Real-time AI insights into patient flow, revenue optimization, and predictive analytics for better decision making.",
    },
    {
      icon: FileText,
      title: "Smart Patient Records",
      description:
        "AI-organized patient histories with automated data extraction, smart categorization, and instant medical record retrieval.",
    },
    {
      icon: CalendarDays,
      title: "Automated Scheduling",
      description:
        "AI-powered appointment optimization that reduces wait times, prevents conflicts, and maximizes clinic efficiency.",
    },
    {
      icon: DollarSign,
      title: "Revenue Intelligence",
      description:
        "AI-driven financial insights, automated billing, and predictive revenue forecasting to maximize profitability.",
    },
    {
      icon: MessageSquare,
      title: "AI Assistant",
      description:
        "24/7 intelligent virtual assistant for patient queries, appointment booking, and administrative tasks automation.",
    },
  ]

  const pricingPlans = [
    {
      name: "Basic",
      price: "Rs 2,000",
      period: "/mo",
      features: ["1 Doctor", "2 Staff Members", "Basic Analytics", "Email Support"],
      popular: false,
    },
    {
      name: "Professional",
      price: "Rs 6,000",
      period: "/mo",
      features: ["3 Doctors", "5 Staff Members", "Advanced Analytics", "Priority Support"],
      popular: true,
    },
    {
      name: "Enterprise",
      price: "Rs 8,000",
      period: "/mo",
      features: ["5 Doctors", "10 Staff Members", "AI Insights", "24/7 Support"],
      popular: false,
    },
    {
      name: "Custom",
      price: "Contact Us",
      period: "",
      features: ["Unlimited Users", "Custom Features", "Dedicated Support", "On-premise Option"],
      popular: false,
    },
  ]

  const testimonials = [
    {
      name: "Dr. Sarah Ahmed",
      specialty: "Cardiologist",
      content:
        "Clinic365 has revolutionized our practice. The AI insights help us make better decisions, and our patient satisfaction has increased by 40%.",
      rating: 5,
    },
    {
      name: "Dr. Michael Chen",
      specialty: "General Practitioner",
      content:
        "The automated scheduling and patient management features have saved us 15 hours per week. It's incredibly efficient.",
      rating: 5,
    },
  ]

  const faqs = [
    {
      question: "How secure is patient data in Clinic365?",
      answer:
        "We use enterprise-grade security with end-to-end encryption, HIPAA compliance, and regular security audits to ensure your patient data is completely secure.",
    },
    {
      question: "Can I access the system from multiple devices?",
      answer:
        "Yes, Clinic365 is cloud-based and can be accessed from any device - mobile, tablet, or desktop - with internet connectivity.",
    },
    {
      question: "Is there a free trial available?",
      answer: "Yes, we offer a 14-day free trial with full access to all features. No credit card required to start.",
    },
    {
      question: "Do you provide training and support?",
      answer:
        "We provide comprehensive training, 24/7 customer support, and dedicated account management for enterprise clients.",
    },
  ]

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Header */}
      <ClassyHeader />
      <main className="flex-grow">
        {/* Hero Section - Side by side layout */}
        <section className="w-full pt-20 pb-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left: Text Content */}
              <div className="space-y-6">
                <div className="flex flex-wrap gap-3">
                  <Badge variant="outline" className="text-sm px-3 py-1 font-medium">
                    Trusted by 10,000+ Healthcare Professionals
                  </Badge>
                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-sm px-3 py-1 font-medium">
                    Y Combinator S24
                  </Badge>
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
                  Pakistan's First AI-Powered <br />
                  <span className="text-blue-600">Clinic Management</span> Platform
                </h1>
                
                <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
                  Transform your healthcare practice with AI-driven insights, automated workflows, and intelligent patient management. Trusted by 10,000+ clinics worldwide.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-base shadow-lg flex items-center gap-2" 
                    onClick={() => navigate("/auth/login")}
                  >
                    Request a Demo <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-6 py-3 text-base border-2 border-blue-600 text-blue-700 hover:bg-blue-50 shadow-lg flex items-center gap-2" 
                    onClick={() => window.open("https://www.youtube.com/watch?v=dQw4w9WgXcQ", "_blank")}
                  >
                    Watch Demo Video <Play className="ml-1 h-4 w-4" />
                  </Button>
                </div>
                
                <div className="flex items-center gap-6 pt-4">
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                      <div className="w-8 h-8 bg-blue-500 rounded-full border-2 border-white"></div>
                      <div className="w-8 h-8 bg-green-500 rounded-full border-2 border-white"></div>
                      <div className="w-8 h-8 bg-purple-500 rounded-full border-2 border-white"></div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-semibold">4.9/5</span>
                      </div>
                      <p className="text-xs">from 2,000+ reviews</p>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    14-day free trial
                  </div>
                </div>
              </div>
              
              {/* Right: Dashboard Image */}
              <div className="relative flex items-center justify-center lg:justify-end">
                <img
                  src="/images/dashboard-overview.png"
                  alt="Dashboard Overview"
                  className="rounded-2xl shadow-2xl border bg-white w-full max-w-2xl h-auto lg:max-w-2xl md:max-w-xl sm:max-w-lg"
                  style={{ objectFit: 'cover' }}
                />
              </div>
            </div>
          </div>
        </section>
        
        {/* Features Section */}
        <section className="w-full py-24 bg-gray-50">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">
                AI-Powered Features
              </Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Everything you need to run a modern clinic</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our AI-powered platform streamlines every aspect of your healthcare practice, from patient management to
                financial analytics.
              </p>
            </div>
            <div className="grid max-w-6xl mx-auto gap-8 lg:grid-cols-3">
              {features.map((feature, index) => (
                <Card key={index} className="bg-white border shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-8">
                    <div className="inline-flex p-3 bg-blue-600 rounded-lg mb-6">
                      <feature.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        
        {/* Dashboard Showcase Section */}
        <DashboardShowcase />
        
        {/* Pricing Section */}
        <section className="w-full py-24">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">
                Pricing Plans
              </Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose the right plan for your clinic</h2>
              <p className="text-xl text-gray-600">Start with our free trial and scale as your practice grows</p>
            </div>
            <div className="grid max-w-6xl mx-auto gap-8 lg:grid-cols-4">
              {pricingPlans.map((plan, index) => (
                <Card
                  key={index}
                  className={`relative ${plan.popular ? "border-blue-500 shadow-lg scale-105" : "border-gray-200"} bg-white`}
                >
                  {plan.popular && (
                    <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white">
                      Most Popular
                    </Badge>
                  )}
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-xl font-semibold">{plan.name}</CardTitle>
                    <div className="text-3xl font-bold text-gray-900">
                      {plan.price}
                      <span className="text-sm text-gray-500 font-normal">{plan.period}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <CheckCircle className="h-4 w-4 text-blue-600" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Link to="/auth/login">
                      <Button
                        className={`w-full ${plan.popular ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-white text-gray-900 border border-gray-200 hover:bg-gray-50"}`}
                      >
                        {plan.name === "Custom" ? "Contact Sales" : "Get Started"}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        
        {/* Testimonials Section */}
        <section className="w-full py-24 bg-gray-50">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">
                Customer Stories
              </Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Trusted by healthcare professionals worldwide</h2>
            </div>
            <div className="grid max-w-4xl mx-auto gap-8 lg:grid-cols-2">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="bg-white border shadow-sm">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {testimonial.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                        <p className="text-sm text-gray-600">{testimonial.specialty}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{testimonial.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="w-full py-24 bg-blue-600">
          <div className="container px-4 md:px-6">
            <div className="text-center space-y-8">
              <h2 className="text-4xl font-bold text-white">Ready to transform your clinic?</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Join thousands of healthcare professionals who have revolutionized their practice with Clinic365.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/auth/login">
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-50 px-8 py-4 text-lg">
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/auth/login">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
                  >
                    Schedule Demo
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-blue-200">14-day free trial • No credit card required • Cancel anytime</p>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="w-full py-24">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">
                FAQ
              </Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently asked questions</h2>
              <p className="text-xl text-gray-600">Everything you need to know about Clinic365</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border">
                  <CardContent className="p-0">
                    <button
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    >
                      <span className="font-semibold text-gray-900">{faq.question}</span>
                      <ChevronDown
                        className={`h-5 w-5 text-gray-500 transition-transform ${openFaq === index ? "rotate-180" : ""}`}
                      />
                    </button>
                    {openFaq === index && (
                      <div className="px-6 pb-6">
                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer className="w-full bg-gray-900 text-white py-16">
        <div className="container px-4 md:px-6">
          <div className="grid gap-8 lg:grid-cols-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Hospital className="h-6 w-6 text-blue-400" />
                <span className="font-semibold text-lg">Clinic365</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Pakistan's first AI-powered clinic management platform for modern healthcare practices.
              </p>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold">Product</h4>
              <div className="space-y-2">
                <Link to="#" className="block text-gray-400 hover:text-white transition-colors text-sm">
                  Features
                </Link>
                <Link to="#" className="block text-gray-400 hover:text-white transition-colors text-sm">
                  Pricing
                </Link>
                <Link to="#" className="block text-gray-400 hover:text-white transition-colors text-sm">
                  API
                </Link>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold">Company</h4>
              <div className="space-y-2">
                <Link to="#" className="block text-gray-400 hover:text-white transition-colors text-sm">
                  About
                </Link>
                <Link to="#" className="block text-gray-400 hover:text-white transition-colors text-sm">
                  Blog
                </Link>
                <Link to="#" className="block text-gray-400 hover:text-white transition-colors text-sm">
                  Careers
                </Link>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold">Support</h4>
              <div className="space-y-2">
                <Link to="#" className="block text-gray-400 hover:text-white transition-colors text-sm">
                  Help Center
                </Link>
                <Link to="#" className="block text-gray-400 hover:text-white transition-colors text-sm">
                  Contact
                </Link>
                <Link to="#" className="block text-gray-400 hover:text-white transition-colors text-sm">
                  Status
                </Link>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-gray-400 text-sm">© 2024 Clinic365. All rights reserved.</p>
              <div className="flex gap-6">
                <Link to="#" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Privacy
                </Link>
                <Link to="#" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Terms
                </Link>
                <Link to="#" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Security
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}